import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const EMPTY = {
  name: "", country: "China", contact_person: "", contact_channel: "wechat",
  contact_value: "", email: "", categories: [], payment_terms: "T/T 30%",
  gst_percent: 0, status: "active", notes: "",
};

export default function Suppliers() {
  const { hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [catText, setCatText] = useState("");

  const load = () => api.get("/suppliers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const canManage = hasRole("admin", "operations");

  const startEdit = (s) => {
    setEditing(s);
    setForm({ ...EMPTY, ...s });
    setCatText((s.categories || []).join(", "));
    setOpen(true);
  };
  const startNew = () => { setEditing(null); setForm(EMPTY); setCatText(""); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, categories: catText.split(",").map((c) => c.trim()).filter(Boolean) };
      if (editing) {
        await api.put(`/suppliers/${editing.id}`, payload);
        toast.success("Supplier updated");
      } else {
        await api.post("/suppliers", payload);
        toast.success("Supplier created");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const filtered = items.filter(
    (s) =>
      s.name?.toLowerCase().includes(q.toLowerCase()) ||
      s.code?.toLowerCase().includes(q.toLowerCase()) ||
      s.country?.toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <div className="space-y-5" data-testid="suppliers-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mini">Supplier Network</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Suppliers</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="supplier-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-9 pl-8 w-72 rounded-sm" />
          </div>
          {canManage && (
            <Button onClick={startNew} data-testid="add-supplier-btn" className="h-9 rounded-sm bg-black hover:bg-zinc-800 text-white">
              <Plus size={14} className="mr-1" /> New Supplier
            </Button>
          )}
        </div>
      </div>

      <div className="card-sharp overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="label-mini px-4 py-3">Code</th>
              <th className="label-mini px-4 py-3">Name</th>
              <th className="label-mini px-4 py-3">Country</th>
              <th className="label-mini px-4 py-3">Categories</th>
              <th className="label-mini px-4 py-3">Contact</th>
              <th className="label-mini px-4 py-3 text-right">Balance (PKR)</th>
              <th className="label-mini px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`supplier-row-${s.code}`}>
                <td className="px-4 py-3 font-mono text-xs">{s.code}</td>
                <td className="px-4 py-3 font-medium">{s.name}</td>
                <td className="px-4 py-3 text-xs">{s.country}</td>
                <td className="px-4 py-3 text-xs">{(s.categories || []).join(", ") || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">
                  {s.contact_channel}: {s.contact_value || "—"}
                </td>
                <td className="px-4 py-3 text-right font-mono tnum">{fmtMoney(s.balance || 0, "")}</td>
                <td className="px-4 py-3 text-right">
                  {canManage && (
                    <button onClick={() => startEdit(s)} className="text-xs font-semibold underline mr-3">Edit</button>
                  )}
                  <Link to={`/suppliers/${s.id}`} className="text-xs font-semibold underline">View</Link>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-zinc-500 text-sm">No suppliers found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="supplier-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Supplier" : "New Supplier"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" required>
              <Input data-testid="sup-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="Country">
              <Input value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="Contact Person">
              <Input value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="Channel">
              <Select value={form.contact_channel} onValueChange={(v) => setForm({ ...form, contact_channel: v })}>
                <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="wechat">WeChat</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Contact Value">
              <Input value={form.contact_value || ""} onChange={(e) => setForm({ ...form, contact_value: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="Email">
              <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <div className="col-span-2">
              <Field label="Categories (comma-separated)">
                <Input value={catText} onChange={(e) => setCatText(e.target.value)} placeholder="electronics, textiles, fittings" className="rounded-sm h-9" />
              </Field>
            </div>
            <Field label="Payment Terms">
              <Input value={form.payment_terms || ""} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="GST % Charged By Supplier">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.gst_percent ?? 0}
                onChange={(e) => setForm({ ...form, gst_percent: parseFloat(e.target.value) || 0 })}
                placeholder="0"
                className="rounded-sm h-9"
                data-testid="supplier-gst-percent"
              />
              <p className="text-xs text-zinc-500 mt-1">
                0 for imports and unregistered vendors. Adds to what we owe them and to your input tax credit.
              </p>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="col-span-2">
              <Field label="Notes">
                <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          className="w-full border border-zinc-200 rounded-sm p-2 text-sm h-20" />
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-supplier-btn" onClick={save} disabled={saving || !form.name} className="rounded-sm bg-black text-white hover:bg-zinc-800">
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="label-mini">{label}{required && <span className="text-red-600">*</span>}</label>
    <div className="mt-1">{children}</div>
  </div>
);
